import type { TextFieldProps } from '@mui/material/TextField';

import dayjs from 'dayjs';
import { useState, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import TextField from '@mui/material/TextField';

// Add at the top of the file
dayjs.extend(customParseFormat);

// ----------------------------------------------------------------------

type Props = TextFieldProps & {
  name: string;
  maxLength?: number;
};

type DateTextFieldProps = TextFieldProps & {
  name: string;
};

export function RHFTimeTextField({ name, helperText, ...other }: DateTextFieldProps) {
  const { control } = useFormContext();
  const [validTime, setValidTime] = useState(true);

  const parseTimeInput = useCallback((value: string) => {
    if (!value) return value;
    const parsed = dayjs(
      value,
      ['h:mm A', 'h:mmA', 'ha', 'h a', 'H:mm', 'HH:mm', 'h:mm a', 'h:mma'],
      true
    );
    return parsed.isValid() ? parsed.format('h:mm A') : '';
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          value={field.value}
          onChange={(event) => {
            const newValue = event.target.value;
            const isValid =
              !newValue ||
              dayjs(
                newValue,
                ['h:mm A', 'h:mmA', 'ha', 'h a', 'H:mm', 'HH:mm', 'h:mm a', 'h:mma'],
                true
              ).isValid();
            setValidTime(isValid);
            field.onChange(newValue);
          }}
          onBlur={(event) => {
            const parsed = parseTimeInput(event.target.value);
            field.onChange(parsed);
            field.onBlur();
          }}
          fullWidth
          error={!!error || !validTime}
          helperText={error?.message ?? (!validTime ? 'Invalid time' : helperText)}
          FormHelperTextProps={{
            sx: { color: 'error.main' },
          }}
          inputProps={{
            autoComplete: 'off',
          }}
          {...other}
        />
      )}
    />
  );
}

export function RHFTextField({ name, helperText, type, maxLength, ...other }: Props) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          fullWidth
          type={type}
          value={type === 'number' && field.value === 0 ? '' : field.value}
          onChange={(event) => {
            if (type === 'number') {
              field.onChange(Number(event.target.value));
            } else {
              field.onChange(event.target.value);
            }
          }}
          error={!!error}
          helperText={
            <>
              {error?.message ?? helperText}
              {maxLength && (
                <span style={{ float: 'right' }}>
                  {field.value?.length || 0}/{maxLength}
                </span>
              )}
            </>
          }
          inputProps={{
            autoComplete: 'off',
            maxLength,
          }}
          {...other}
        />
      )}
    />
  );
}
